import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { articles, type Article } from '@/content/articles';

function ArticleBody({ article }: { article: Article }) {
  return (
    <CardContent className="flex h-full flex-col p-8">
      <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.18em] text-ink/45">
        <span>{article.topic}</span>
        <span aria-hidden="true">/</span>
        <span>{article.status}</span>
      </div>
      <h3 className="mt-5 font-display text-2xl leading-tight tracking-tight">
        {article.title}
      </h3>
      <p className="mt-4 text-ink/70">{article.dek}</p>
      <div className="mt-8 border-t border-ink/10 pt-5 text-sm text-ink/60">
        <p className="font-medium text-ink/75">{article.readTime}</p>
        <p className="mt-2">{article.disclosure}</p>
      </div>
      {article.href && (
        <div className="mt-6">
          <Button asChild size="sm" variant="outline">
            <Link to={article.href}>Read →</Link>
          </Button>
        </div>
      )}
    </CardContent>
  );
}

const disclosurePoints = [
  'Articles may use AI-assisted research, outlining, drafting, and editing.',
  'Final claims, examples, boundaries, and published judgment remain JL-reviewed.',
  'Private client records, raw financial data, screenshots, and confidential source files stay out of public writing unless explicitly approved and sanitized.',
];

export default function Articles() {
  return (
    <div className="bg-paper">
      <section id="articles-start" data-guide-label="Articles" className="border-b border-ink/5">
        <div className="mx-auto max-w-5xl px-6 py-20 md:py-24">
          <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
            Articles
          </p>
          <div className="mt-3 grid gap-8 md:grid-cols-[1.1fr_0.9fr] md:items-end">
            <div>
              <h1 className="font-display text-4xl leading-tight tracking-tight md:text-6xl">
                Public notes on finance, systems, and AI-assisted work.
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink/70">
                This hub is prepared for future essays and practical articles.
                The writing will stay useful, transparent, and careful around
                client privacy.
              </p>
            </div>
            <Card className="border-accent/40 bg-white/70">
              <CardContent className="p-6">
                <p className="font-display text-xs uppercase tracking-[0.2em] text-accent">
                  AI use disclosure
                </p>
                <p className="mt-3 text-sm leading-relaxed text-ink/75">
                  JL Patawaran is an AI-Augmented Finance Professional.
                  Articles may be supported by AI for research, drafting,
                  editing, and structure. The final published view remains
                  human-reviewed and responsibility-owned.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section id="article-pipeline" data-guide-label="Pipeline" className="border-b border-ink/5">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
                Article pipeline
              </p>
              <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
                Starter topics, not publication claims.
              </h2>
            </div>
            <Button asChild variant="outline">
              <Link to="/work-with-me#prefit">Start async prefit</Link>
            </Button>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {articles.map((article) => (
              <article key={article.slug}>
                <Card className="h-full">
                  <ArticleBody article={article} />
                </Card>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="publishing-standard" data-guide-label="Standard" className="bg-ink text-paper">
        <div className="mx-auto grid max-w-5xl gap-10 px-6 py-20 md:grid-cols-[0.85fr_1.15fr]">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
              Publishing standard
            </p>
            <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
              Transparent AI help. Human-owned judgment.
            </h2>
          </div>
          <ul className="grid gap-4 text-paper/75">
            {disclosurePoints.map((point) => (
              <li key={point} className="flex gap-3 border-b border-paper/10 pb-4">
                <span className="mt-1 text-accent">-</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
